import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DialogAddCommentData } from 'src/app/components/dialogs/add-comment/models/dialog-add-comment-data';
import { CommentInput } from 'src/app/models/server/comment-input';
import { Contact } from 'src/app/models/server/contact';
import { Folder } from 'src/app/models/server/folder';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { FolderWorkflowStepsSnapshot } from 'src/app/models/server/folder-workflow-steps-snapshot';
import { History } from 'src/app/models/server/history';
import { Reminder } from 'src/app/models/server/reminder';
import { StandByDatas } from 'src/app/models/server/stand-by-datas';
import { FolderActionsService } from 'src/app/services/actions/folder-actions.service';
import { ContactsService } from 'src/app/services/http/contacts.service';
import { FolderCommentsService } from 'src/app/services/http/folder-comments.service';
import { FolderDocumentsService } from 'src/app/services/http/folder-documents.service';
import { FolderHistoriesService } from 'src/app/services/http/folder-histories.service';
import { RemindersService } from 'src/app/services/http/reminders.service';
import { DocumentsStateService } from 'src/app/services/state/documents-state.service';
import { FavoriteDocumentsStateService } from 'src/app/services/state/favorite-documents-state.service';
import { RemindersStateService } from 'src/app/services/state/reminders-state.service';
import { DetailBaseDatasource } from './detail-base-datasource';

@Injectable()
export class DetailDatasource extends DetailBaseDatasource {
    constructor(
        protected foldersCommentsService: FolderCommentsService,
        protected remindersService: RemindersService,
        protected folderActionsService: FolderActionsService,
        protected remindersStateService: RemindersStateService,
        protected documentsStateService: DocumentsStateService,
        protected folderDocumentsService: FolderDocumentsService,
        protected favoriteStateService: FavoriteDocumentsStateService,
        protected contactsService: ContactsService,
        protected foldersHistoriesService: FolderHistoriesService
    ) {
        super();
    }

    /**
     * Supprime le dossier spécifié.
     */
    public override deleteFolder(folder: Folder) {
        this.folderActionsService.deleteFolder(folder);
    }

    /**
     * Place le dossier spécifié en veille.
     */
    public override setFolderInStandByState(folder: Folder, data: StandByDatas) {
        this.folderActionsService.setInStandByState(folder, data);
    }

    /**
     * Sort le dossier spécifié de la veille
     */
    public override removeFolderFromStandByState(folder: Folder) {
        this.folderActionsService.removeFromStandByState(folder);
    }

    /**
     * Place le dossier spécifié en 'attente d'archivage'.
     */
    public override setFolderInWaitingArchivingState(folder: Folder): void {
        this.folderActionsService.SetInWaitingArchivingState(folder);
    }

    /**
     * Retire le dossier spécifié de l'état en 'attente d'archivage'.
     */
    public override removeFolderFromWaitingArchivingState(folder: Folder): void {
        this.folderActionsService.removeFromWaitingArchivingState(folder);
    }

    /**
     * Liste les documents d'un dossier.
     * @param viewId
     * @param folderId
     */
    public override listDocuments(viewId: number, folderId: string): Observable<FolderDocument[]> {
        return this.folderDocumentsService.list(viewId, folderId);
    }

    /**
     * Supprime le document spécifié.
     */
    public override deleteDocument(viewId: number, folderId: string, documentId: string): Observable<boolean> {
        return this.folderDocumentsService.delete(viewId, folderId, documentId);
    }

    /**
     * Ajoute un commentaire au dossier.
     */
    public override addComment(viewId: number, folderId: string, data: DialogAddCommentData): Observable<FolderDocument> {
        return this.foldersCommentsService.add(viewId, folderId, data);
    }

    /**
     * Met à jour un commentaire d'un dossier.
     */
    public override updateComment(viewId: number, folderId: string, comment: CommentInput): Observable<FolderDocument> {
        return this.foldersCommentsService.update(viewId, folderId, comment);
    }

    /**
     * Supprime un commentaire d'un dossier.
     * @param viewId
     * @param reminder
     */
    public override deleteComment(viewId: number, folderId: string, comment: CommentInput): Observable<FolderDocument> {
        return this.foldersCommentsService.delete(viewId, folderId, comment);
    }

    /**
     * Ajoute un rappel au dossier.
     */
    public override addReminder(viewId: number, folderId: string, data: Reminder): Observable<Reminder> {
        return this.remindersService.add(viewId, folderId, data);
    }

    /**
     * Met à jour un rappel d'un dossier.
     * @param viewId
     * @param folderId
     * @param data
     * @returns
     */
    public override updateReminder(viewId: number, folderId: string, data: Reminder): Observable<Reminder> {
        return this.remindersService.update(viewId, folderId, data);
    }

    /**
     * Supprime un rappel d'un dossier.
     * @param viewId
     * @param reminder
     */
    public override deleteReminder(viewId: number, reminder: Reminder): Observable<boolean> {
        return this.remindersService.delete(viewId, reminder);
    }

    /**
     * Liste les rappels d'un dossier.
     * @param viewId
     * @param folderId
     */
    public override listReminders(viewId: number, folderId: string): Observable<Reminder[]> {
        return this.remindersService.List(viewId, folderId);
    }

    /**
     * Liste les entrées de l'historique d'un dossier.
     * @param viewId
     * @param folderId
     */
    public override listHistories(viewId: number, folderId: string): Observable<History[]> {
        return this.foldersHistoriesService.list(viewId, folderId);
    }

    /**
     * Marque un document comme 'lu' ou comme 'non lu'.
     * @param viewId
     * @param folderId
     * @param documentId
     * @param isRead
     */
    public override markDocumentAsRead(viewId: number, folderId: string, documentId: string, isRead: boolean): Observable<FolderDocument> {
        return this.folderDocumentsService.markIsRead(viewId, folderId, documentId, isRead);
    }

    /**
     * Marque un dossier comme 'lu' ou comme 'non lu'.
     * @param folder
     * @param isRead
     */
    public override markFolderAsRead(folder: Folder, isRead: boolean) {
        this.folderActionsService.toggleReadStatus(folder, isRead);
    }

    /**
     * Ajoute un contact au carnet d'adresses.
     * @param contact
     */
    public override addContact(contact: Contact) {
        return this.contactsService.add(contact);
    }

    /**
     * Déplace un dossier dans une bannette.
     * @param viewId
     * @param folder
     * @param boxId
     */
    public override moveFolderToBox(viewId: number, folder: Folder, boxId: string) {
        return this.folderActionsService.moveToBox(viewId, folder, boxId);
    }

    /**
     * Met à jour un attribut d'un dossier.
     * @param viewId
     * @param folder
     * @param attributeId
     * @param oldValue
     * @param newValue
     */
    public override updateFolderAttribute(viewId: number, folder: Folder, attributeId: number, oldValue: any, newValue: any) {
        return this.folderActionsService.updateFolderAttribute(viewId, folder, attributeId, oldValue, newValue);
    }

    /**
     * Met à jour l'état d'une étape du workflow.
     * @param viewId
     * @param folderId
     * @param workflowStepsSnapshot
     * @param newStateId
     */
    public override updateFolderWorkflowStepState(viewId: number, folderId: string, workflowStepsSnapshot: FolderWorkflowStepsSnapshot, newStateId: number) {
        this.folderActionsService.updateFolderWorkflowStepState(viewId, folderId, workflowStepsSnapshot, newStateId);
    }

    /**
     * Active une étape de workflow d'un dossier.
     * @param workflowStepsSnapshot : Instance de l'étape du workflow.
     */
    public override activateFolderWorkflowStep(viewId: number, folderId: string, workflowsStepId: number): void {
        this.folderActionsService.activateFolderWorkflowStep(viewId, folderId, workflowsStepId);
    }

    /**
     * Finalise une étape de workflow d'un dossier.
     * @param viewId
     * @param folderId
     * @param workflowStepsSnapshot
     */
    public override completeFolderWorkflowStep(viewId: number, folderId: string, workflowStepsSnapshot: FolderWorkflowStepsSnapshot): void {
        this.folderActionsService.completeFolderWorkflowStep(viewId, folderId, workflowStepsSnapshot);
    }

    /**
     * Archive le dossier.
     * @param folder Instance du dossier.
     */
    public override archive(folder: Folder): void {
        this.folderActionsService.archive(folder);
    }

    /**
     * Suit le dossier.
     * @param folder Instance du dossier.
     */
    public override followFolder(folder: Folder): void {
        this.folderActionsService.followFolder(folder);
    }

    /**
     * Arrête le suivi du dossier.
     * @param folder Instance du dossier.
     */
    public override stopFollowFolder(folder: Folder): void {
        this.folderActionsService.stopFollowFolder(folder);
    }
}
